import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { attendanceAPI } from '../services/api';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Loader2, Save, ShieldCheck, MapPin, Camera, AlertTriangle } from 'lucide-react';

const AttendanceSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [s, setS] = useState({ require_gps: true, require_photo: true, enforce_geofence: false, max_geofence_distance_m: 500 });

  useEffect(() => {
    (async () => {
      try {
        const r = await attendanceAPI.getSettings();
        setS({
          require_gps: r.data.require_gps ?? true,
          require_photo: r.data.require_photo ?? true,
          enforce_geofence: r.data.enforce_geofence ?? false,
          max_geofence_distance_m: r.data.max_geofence_distance_m ?? 500,
        });
      } catch (e) { toast.error('Failed to load settings'); }
      finally { setLoading(false); }
    })();
  }, []);

  // Role guard — only admin roles can view this page. Hooks must run first (rules-of-hooks).
  if (user && !['super_admin', 'hr_admin'].includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  const save = async () => {
    setSaving(true);
    try {
      await attendanceAPI.updateSettings({ ...s, max_geofence_distance_m: parseInt(s.max_geofence_distance_m) || 500 });
      toast.success('Attendance settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="attendance-settings-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck />Attendance Policy</h1>
        <p className="text-muted-foreground">Control what employees must provide when punching in / out</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Required Captures</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg" data-testid="toggle-gps-row">
            <div className="flex items-start gap-3">
              <MapPin className="text-blue-600 mt-1" size={20} />
              <div>
                <p className="font-medium">Mandatory GPS Location</p>
                <p className="text-xs text-muted-foreground">Block punches when GPS coordinates are missing. Browser will prompt the employee for location access.</p>
              </div>
            </div>
            <Switch checked={s.require_gps} onCheckedChange={(v) => setS({ ...s, require_gps: v })} data-testid="toggle-require-gps" />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg" data-testid="toggle-photo-row">
            <div className="flex items-start gap-3">
              <Camera className="text-emerald-600 mt-1" size={20} />
              <div>
                <p className="font-medium">Mandatory Live Photo</p>
                <p className="text-xs text-muted-foreground">Require employees to capture a live selfie at every punch. Embedded timestamp watermark prevents reuse of old photos.</p>
              </div>
            </div>
            <Switch checked={s.require_photo} onCheckedChange={(v) => setS({ ...s, require_photo: v })} data-testid="toggle-require-photo" />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg" data-testid="toggle-geofence-row">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-600 mt-1" size={20} />
              <div>
                <p className="font-medium">Enforce Company Geofence</p>
                <p className="text-xs text-muted-foreground">Reject punches when the employee is outside every enabled geofence for their company. Set up zones in the <strong>Geofences</strong> page first.</p>
              </div>
            </div>
            <Switch checked={s.enforce_geofence} onCheckedChange={(v) => setS({ ...s, enforce_geofence: v })} data-testid="toggle-enforce-geofence" />
          </div>

          {s.enforce_geofence && (
            <div className="ml-9 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Default radius fallback (metres)</Label>
                <Input
                  type="number"
                  value={s.max_geofence_distance_m}
                  onChange={(e) => setS({ ...s, max_geofence_distance_m: e.target.value })}
                  data-testid="geofence-radius-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Used only when a geofence has no radius defined.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-amber-50 dark:bg-amber-950/20 border-amber-200">
        <CardContent className="p-4 text-sm">
          <p className="font-medium flex items-center gap-2 text-amber-700 dark:text-amber-300"><AlertTriangle size={14} />Browser requirements</p>
          <ul className="list-disc pl-5 mt-1 text-amber-700/80 dark:text-amber-300/80 text-xs space-y-1">
            <li>GPS and camera APIs only work over <strong>HTTPS</strong> (or <code>localhost</code>). Ensure your production deployment uses a valid TLS certificate.</li>
            <li>Employees must explicitly grant permission the first time. If denied, the system shows a clear retry prompt.</li>
            <li>The capture timestamp is overlaid on every photo before upload to prevent reuse.</li>
          </ul>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-attendance-settings">
          {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}Save Policy
        </Button>
      </div>
    </div>
  );
};

export default AttendanceSettings;
